import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import styles from './SpellingModes.module.css';
import AITutor from '../AITutor/AITutor';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaVolumeUp, FaPlay, FaPause } from 'react-icons/fa';
import { API_BASE_URL, apiRequest } from '../../config/api';
import Loader from '../common/Loader/Loader';

const AudioSpellingMode = () => {
  const { userProgress, loading: userLoading, error: userError, updateProgress } = useUser();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playCount, setPlayCount] = useState(0);
  const initialized = useRef(false);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [completingSession, setCompletingSession] = useState(false);

  const navigate = useNavigate();
  const { state } = useLocation();
  const difficulty = state?.difficulty || userProgress?.modeLevels?.audio_spelling || 1;

  // Speech synthesis setup
  const synth = window.speechSynthesis;
  const [selectedVoice, setSelectedVoice] = useState(null);

  // Initialize voice when component mounts
  useEffect(() => {
    const initVoice = () => {
      const voices = synth.getVoices();
      // Prioritize British English voices
      const britishVoice = voices.find(voice => voice.lang === 'en-GB');
      const englishVoice = voices.find(voice => voice.lang.startsWith('en-'));
      
      if (britishVoice) {
        setSelectedVoice(britishVoice);
      } else if (englishVoice) {
        setSelectedVoice(englishVoice);
      }
    };

    if (synth.onvoiceschanged !== undefined) {
      synth.onvoiceschanged = initVoice;
    }
    initVoice();
  }, []);

  useEffect(() => {
    if (!userLoading && userProgress && !initialized.current) {
      startPractice();
      initialized.current = true;
    }
  }, [userLoading, userProgress]);

  useEffect(() => {
    if (questions.length > 0) {
      setQuestionStartTime(Date.now());
      setUserInput('');
      setShowExplanation(false);
      setFeedback(null);
      setPlayCount(0);
      setIsPlaying(false);
    }
  }, [currentQuestion, questions]);

  const handlePlayAudio = () => {
    if (isPlaying) {
      synth.cancel();
      setIsPlaying(false);
      return;
    }

    if (questions[currentQuestion]) {
      const utterance = new SpeechSynthesisUtterance(questions[currentQuestion].content.correctAnswer);
      
      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }
      
      utterance.rate = 0.7; // Even slower for better clarity
      utterance.pitch = 1;
      
      utterance.onend = () => {
        setIsPlaying(false);
      };

      utterance.onerror = (event) => {
        console.error('Speech synthesis error:', event);
        setIsPlaying(false);
        setError('Failed to play audio. Please try again.');
      };

      setIsPlaying(true);
      setPlayCount(prev => prev + 1); // Keep track but don't limit
      synth.speak(utterance);
    }
  };

  const startPractice = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        mode: 'audio_spelling',
        difficulty: difficulty
      });

      const questionsData = await apiRequest(`/spelling/questions?${params}`);

      const sessionData = await apiRequest('/sessions/start', {
        method: 'POST',
        body: JSON.stringify({
          mode: 'audio_spelling',
          difficulty: difficulty,
          questions: questionsData.map(q => q._id)
        })
      });

      setQuestions(questionsData);
      localStorage.setItem('currentSessionId', sessionData._id);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !userInput.trim()) return;
    setIsSubmitting(true);

    try {
      const sessionId = localStorage.getItem('currentSessionId');
      const currentQ = questions[currentQuestion];
      const timeSpent = (Date.now() - questionStartTime) / 1000;
      
      const submitResponse = await apiRequest(`/sessions/${sessionId}/answer`, {
        method: 'POST',
        body: JSON.stringify({
          questionId: currentQ._id,
          answer: userInput.trim(),
          timeSpent,
          metadata: {
            playCount,
            // audioLength: audioRef.current?.duration || 0
          }
        })
      });

      setFeedback({
        isCorrect: submitResponse.isCorrect,
        message: submitResponse.isCorrect ? 'Correct spelling!' : 'Incorrect. Listen again and try one more time.'
      });
      setShowExplanation(true);

    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    setShowExplanation(false);
    
    if (currentQuestion + 1 >= questions.length) {
      try {
        setCompletingSession(true);
        const sessionId = localStorage.getItem('currentSessionId');
        
        const results = await apiRequest(`/sessions/${sessionId}/end`, {
          method: 'POST'
        });

        localStorage.removeItem('currentSessionId');
        updateProgress(results);
        
        // Navigate to results page with session results and questions
        navigate('/results', { 
          state: { 
            sessionResults: results,
            questions: questions
          } 
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setCompletingSession(false);
      }
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  if (loading || userLoading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className={styles.container}>
      {completingSession && (
        <div className={styles.overlay}>
          <Loader size="large" text="Analyzing your results..." />
        </div>
      )}
      <div className={styles.card}>
        <AITutor 
          currentQuestion={currentQ}
          userAnswer={userInput}
          isCorrect={feedback?.isCorrect}
        />
        
        <div className={styles.header}>
          <h2 className={styles.title}>Audio Spelling</h2>
          <span className={styles.questionCount}>
            Question {currentQuestion + 1} of {questions.length}
          </span>
        </div>

        <div className={styles.progressContainer}>
          <div className={styles.progressBar} style={{ width: `${progress}%` }} />
        </div>

        <div className={styles.audioSection}>
          <button 
            onClick={handlePlayAudio}
            className={styles.audioButton}
            disabled={showExplanation}
          >
            {isPlaying ? <FaPause /> : <FaVolumeUp />}
            {isPlaying ? 'Stop' : 'Play Word'}
          </button>
          <span className={styles.playCount}>
            Plays: {playCount}
          </span>
        </div>

        <form onSubmit={handleSubmit} className={styles.inputForm}>
          <input
            type="text"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            className={styles.answerInput}
            placeholder="Type the word you hear..."
            disabled={showExplanation}
          />
          {!showExplanation && (
            <button 
              type="submit" 
              className={styles.submitButton}
              disabled={isSubmitting || !userInput.trim()}
            >
              Submit
            </button>
          )}
        </form>

        {feedback && (
          <div className={`${styles.feedback} ${feedback.isCorrect ? styles.correct : styles.incorrect}`}>
            {feedback.message}
          </div>
        )}

        {showExplanation && (
          <>
            <div className={styles.explanation}>
              <p>Correct spelling: {currentQ.content.correctAnswer}</p>
              <p>Pronunciation: {currentQ.content.pronunciation}</p>
              <p>{currentQ.content.explanation}</p>
            </div>
            <button 
              className={styles.nextButton}
              onClick={handleNext}
            >
              {currentQuestion + 1 >= questions.length ? 'Finish' : 'Next Word'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AudioSpellingMode; 